import { Box, Typography } from '@mui/material';
import React, { useEffect } from 'react';

import { useInstallationGuide } from '../../../hooks/useInstallationGuide';
import { GuidanceSection } from '../GuidanceSection';
import { WizardNavigation } from '../WizardNavigation';

/** Path to the router plug animation */
const ROUTER_PLUG_GIF_PATH = '/assets/images/installation-guide/router-plug.gif';

/**
 * ConnectRouterStep - Slide 2.6: Connect Ethernet to Router
 *
 * Shows instructions for plugging the network cable into the router:
 * - Animated illustration of ethernet cable being plugged into router
 * - Two chatbot help buttons for router identification and port issues
 *
 * Note: This is part of "Step 5 of 9" (same as ConnectOverviewStep - they are sub-steps)
 */
export const ConnectRouterStep: React.FC = () => {
  const { goToStep, setMainStepId } = useInstallationGuide();

  useEffect(() => {
    setMainStepId('step_cableToRouter');
  }, [setMainStepId]);

  const handleContinue = () => {
    goToStep('connect-core');
  };

  const guidanceButtons = [
    { label: __('Help me identify my router'), flowId: 'identify-router' },
    { label: __('There are no open ports'), flowId: 'no-open-ports' },
  ];

  return (
    <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
      <Typography variant="h3" component="h1" sx={{ mb: 1, textAlign: 'center' }}>
        {__('Connect ethernet to router.')}
      </Typography>

      <Typography variant="body1" sx={{ mb: 3, color: 'text.secondary' }}>
        {__('Plug the provided network cable into an open port on your router.')}
      </Typography>

      {/* Router plug animation */}
      <Box
        sx={{
          flex: 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          my: 2,
        }}
      >
        <Box
          component="img"
          src={ROUTER_PLUG_GIF_PATH}
          alt={__('Plugging ethernet cable into router')}
          sx={{
            maxWidth: '100%',
            maxHeight: 280,
            objectFit: 'contain',
          }}
        />
      </Box>

      {/* Guidance section with chatbot buttons */}
      <GuidanceSection
        buttons={guidanceButtons}
      />

      <WizardNavigation
        primaryLabel={__('Continue')}
        onPrimary={handleContinue}
      />
    </Box>
  );
};
